/**
 * @file menu.ts
 * @description Меню сайта: типы, строки черновика ↔ дерево снимка, проверка
 * (phase19.16 Р1; SB15 — меню шапки и подвала, два уровня, мобильное
 * собирается само; SB16 — пункт без цели скрывается на сайте; SB81 — пункт
 * можно скрыть без удаления; db-design-r1 §1, §2 спорное место 2, §7 риск 2).
 *
 * Черновик — строки `SiteDraftMenuItem` с FK (кабинет), опубликованное —
 * дерево без id строк внутри `SiteRevision.menus` (витрина). Одни и те же
 * функции строят снимок при публикации и разворачивают его при «Отменить
 * изменения» / «Вернуть версию», поэтому «строки → дерево → строки → дерево»
 * обязано давать то же дерево (тест туда-обратно).
 *
 * Проверка структуры повторяет CHECK `SiteDraftMenuItem_target_check` и
 * добавляет то, что CHECK выразить не может (уровни, колонки подвала).
 * Часть меню, ссылки которой собираются из живых данных (адреса страниц,
 * категорий), — дело сервера: здесь только id целей.
 */

import { z } from "zod";

import { footerLayout } from "./footer-layout.js";
import { SITE_LIMITS } from "./limits.js";

export const SITE_MENU_LOCATIONS = ["HEADER", "FOOTER"] as const;
export type SiteMenuLocation = (typeof SITE_MENU_LOCATIONS)[number];

/** Совпадает с enum `SiteMenuLinkType`. */
export const SITE_MENU_LINK_TYPES = [
  "GROUP",
  "SITE_PAGE",
  "CATEGORY",
  "CATALOG_CATEGORIES",
  "CONTENT_CATEGORY",
  "CONTENT_TAG",
  "LEGAL_DOCUMENT",
  "SYSTEM",
  "URL",
] as const;
export type SiteMenuLinkType = (typeof SITE_MENU_LINK_TYPES)[number];

/** Разделы платформы для пунктов меню (enum `SiteSystemRoute`). */
export const SITE_SYSTEM_ROUTES = ["HOME", "CATALOG", "BLOG"] as const;
export type SiteSystemRoute = (typeof SITE_SYSTEM_ROUTES)[number];

/** Тип ссылки → поле с id цели (FK, SET NULL при удалении цели). */
export const SITE_MENU_TARGET_FIELD = {
  SITE_PAGE: "sitePageId",
  CATEGORY: "categoryId",
  CONTENT_CATEGORY: "contentCategoryId",
  CONTENT_TAG: "contentTagId",
  LEGAL_DOCUMENT: "legalDocumentId",
} as const satisfies Partial<Record<SiteMenuLinkType, string>>;

type TargetLinkType = keyof typeof SITE_MENU_TARGET_FIELD;
type TargetField = (typeof SITE_MENU_TARGET_FIELD)[TargetLinkType];

const TARGET_FIELDS: readonly TargetField[] = Object.values(SITE_MENU_TARGET_FIELD);

/** Поля пункта без положения в дереве — общие для строки и узла снимка. */
export interface SiteMenuNodeFields {
  /** null допустим только у GROUP (колонка без заголовка). */
  label: string | null;
  linkType: SiteMenuLinkType;
  sitePageId: string | null;
  categoryId: string | null;
  contentCategoryId: string | null;
  contentTagId: string | null;
  legalDocumentId: string | null;
  systemRoute: SiteSystemRoute | null;
  url: string | null;
  /** Только CATALOG_CATEGORIES; null — сколько есть, но не больше лимита. */
  autoLimit: number | null;
  openInNewTab: boolean;
  /** SB81: скрыт с сайта, но не удалён. */
  isHidden: boolean;
}

/** Строка черновика `SiteDraftMenuItem` (кабинет, сериализатор). */
export interface SiteMenuRow extends Omit<SiteMenuNodeFields, "isHidden"> {
  id: string;
  location: SiteMenuLocation;
  parentId: string | null;
  sortOrder: number;
  /**
   * SB81. Необязательно, пока в схеме нет колонки (отсутствие = показан);
   * дерево всегда несёт явное значение.
   */
  isHidden?: boolean;
}

/** Узел снимка: без id строки и порядка — порядок задаёт массив. */
export interface SiteMenuNode extends SiteMenuNodeFields {
  children: SiteMenuNode[];
}

/** Снимок меню (`SiteRevision.menus`): мобильное собирается из обоих (SB15). */
export type SiteMenus = Record<SiteMenuLocation, SiteMenuNode[]>;

/** Пустое меню обоих мест. */
export function emptySiteMenus(): SiteMenus {
  return { HEADER: [], FOOTER: [] };
}

const nodeFields = (row: SiteMenuRow): SiteMenuNodeFields => ({
  label: row.label,
  linkType: row.linkType,
  sitePageId: row.sitePageId,
  categoryId: row.categoryId,
  contentCategoryId: row.contentCategoryId,
  contentTagId: row.contentTagId,
  legalDocumentId: row.legalDocumentId,
  systemRoute: row.systemRoute,
  url: row.url,
  autoLimit: row.autoLimit,
  openInNewTab: row.openInNewTab,
  isHidden: row.isHidden ?? false,
});

/**
 * Строки черновика → дерево снимка. Соседи — по `sortOrder`, при равенстве
 * по id (детерминированно: снимок сравнивается с прошлой ревизией).
 * Родитель из другого места, несуществующий родитель или цикл — нарушение
 * инварианта БД (каскад и проверка сервиса), это ошибка, а не «пропустить».
 */
export function menuRowsToTree(rows: readonly SiteMenuRow[]): SiteMenus {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const children = new Map<string | null, SiteMenuRow[]>();
  for (const row of rows) {
    if (row.parentId !== null) {
      const parent = byId.get(row.parentId);
      if (!parent || parent.location !== row.location) {
        throw new Error(`site_menu_parent_invalid: ${row.id}`);
      }
    }
    const list = children.get(row.parentId) ?? [];
    list.push(row);
    children.set(row.parentId, list);
  }
  const ordered = (list: SiteMenuRow[] | undefined): SiteMenuRow[] =>
    [...(list ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  let visited = 0;
  const build = (row: SiteMenuRow): SiteMenuNode => {
    visited += 1;
    return { ...nodeFields(row), children: ordered(children.get(row.id)).map(build) };
  };
  const menus = emptySiteMenus();
  for (const row of ordered(children.get(null))) menus[row.location].push(build(row));
  // Строки, не достижимые от корней, бывают только при цикле parentId.
  if (visited !== rows.length) throw new Error("site_menu_cycle");
  return menus;
}

/**
 * Дерево снимка → строки черновика. `newId` — фабрика id (cuid на сервере,
 * счётчик в тестах): время и случайность — параметром. `sortOrder` — номер
 * среди соседей с нуля.
 */
export function menuTreeToRows(menus: SiteMenus, newId: () => string): SiteMenuRow[] {
  const rows: SiteMenuRow[] = [];
  const walk = (
    nodes: readonly SiteMenuNode[],
    location: SiteMenuLocation,
    parentId: string | null,
  ): void => {
    nodes.forEach((node, index) => {
      const { children, ...fields } = node;
      const id = newId();
      rows.push({ ...fields, id, location, parentId, sortOrder: index });
      walk(children, location, id);
    });
  };
  for (const location of SITE_MENU_LOCATIONS) walk(menus[location], location, null);
  return rows;
}

/** Колонка подвала — группа или автосписок категорий (SB113). */
const isFooterColumnType = (linkType: SiteMenuLinkType): boolean =>
  linkType === "GROUP" || linkType === "CATALOG_CATEGORIES";

/**
 * П3 / SB113: одиночные ссылки верхнего уровня подвала → одна колонка
 * без заголовка. То же правило, что в data-миграции
 * `20261012150200_phase19_16_footer_loose_items_to_group`, — применяется при
 * записи снимка в черновик («Отменить изменения», «Вернуть версию», импорт,
 * создание черновика): старые снимки ещё несут одиночные ссылки, а запись
 * меню из кабинета их отклоняет (`site_menu_footer_loose_item`).
 *
 *   - колонка: GROUP, `label` null, все цели null, видима; `sortOrder` —
 *     первой одиночной ссылки (там же витрина рисовала их колонкой —
 *     `footerLayout`, вид подвала не меняется);
 *   - одиночные ссылки по порядку (`sortOrder`, затем `id`) переходят в
 *     колонку с `sortOrder` 0..N-1; дети такой ссылки встают в ту же колонку
 *     сразу после неё (третьего уровня нет, SB15);
 *   - шапка, колонки и их дети не меняются; одиночных ссылок нет — строки
 *     возвращаются как есть, `newId` не вызывается (повтор ничего не делает).
 *
 * `newId` — параметром: id колонки задаёт вызывающий (сервер — uuid, тест —
 * счётчик).
 */
export function wrapFooterLooseItems(rows: readonly SiteMenuRow[], newId: () => string): SiteMenuRow[] {
  const bySortThenId = (a: SiteMenuRow, b: SiteMenuRow): number =>
    a.sortOrder - b.sortOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const loose = rows
    .filter((r) => r.location === "FOOTER" && r.parentId === null && !isFooterColumnType(r.linkType))
    .sort(bySortThenId);
  const first = loose[0];
  if (!first) return rows.map((r) => ({ ...r }));

  const groupId = newId();
  // Новый порядок внутри колонки: ссылка, затем её дети.
  const columnOrder = new Map<string, number>();
  for (const link of loose) {
    columnOrder.set(link.id, columnOrder.size);
    for (const child of rows.filter((r) => r.parentId === link.id).sort(bySortThenId)) {
      columnOrder.set(child.id, columnOrder.size);
    }
  }
  const column: SiteMenuRow = {
    id: groupId,
    location: "FOOTER",
    parentId: null,
    sortOrder: first.sortOrder,
    label: null,
    linkType: "GROUP",
    sitePageId: null,
    categoryId: null,
    contentCategoryId: null,
    contentTagId: null,
    legalDocumentId: null,
    systemRoute: null,
    url: null,
    autoLimit: null,
    openInNewTab: false,
    isHidden: false,
  };
  // В columnOrder — только одиночные ссылки и их дети: они и переезжают в колонку.
  const moved = rows.map((r): SiteMenuRow => {
    const order = columnOrder.get(r.id);
    return order === undefined ? { ...r } : { ...r, parentId: groupId, sortOrder: order };
  });
  return [...moved, column];
}

/**
 * Адрес пункта «URL»: путь сайта `/…` или внешний `https://…` (uiux-design-r1
 * §3: «Адрес должен начинаться с https://»). `//host` и `http:` не принимаются.
 */
export function isSafeMenuUrl(url: string): boolean {
  if (url.length === 0 || url.length > 2000) return false;
  if (url.startsWith("/")) return !url.startsWith("//") && !/\s/.test(url);
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Пункт-«сирота»: тип с целью, а цель удалена (FK SET NULL). На сайте
 * скрывается, в кабинете помечается (SB16).
 */
export function isMenuNodeOrphan(node: SiteMenuNodeFields): boolean {
  if (!(node.linkType in SITE_MENU_TARGET_FIELD)) return false;
  const field = SITE_MENU_TARGET_FIELD[node.linkType as TargetLinkType];
  return node[field] === null;
}

/** Замечание проверки меню. */
export interface SiteMenuIssue {
  /** Путь: `HEADER.0.children.1`. */
  path: string;
  code:
    | "site_menu_label_required"
    | "site_menu_label_too_long"
    | "site_menu_target_mismatch"
    | "site_menu_url_invalid"
    | "site_menu_system_route_required"
    | "site_menu_auto_limit_invalid"
    | "site_menu_too_deep"
    | "site_menu_group_nested"
    | "site_menu_auto_list_children"
    | "site_menu_footer_columns"
    | "site_menu_footer_loose_item";
  message: string;
}

/**
 * П3 / SB113: в подвале нет одиночных ссылок верхнего уровня — только
 * колонки (группа или автосписок категорий). Включено вместе с data-миграцией
 * `20261012150200_phase19_16_footer_loose_items_to_group` (одиночные ссылки
 * FOOTER → колонка без заголовка): проверка меню идёт по всему месту, и без
 * миграции любая правка подвала у магазина с одиночными ссылками падала бы.
 * Чтение сохранённых снимков и импорт эту проверку не делают никогда (откат
 * к старой версии не должен терять меню).
 */
export const SITE_MENU_FOOTER_REJECT_LOOSE = true;

/**
 * Режим проверки меню:
 *   - `write` — то, что пишет кабинет: колонки подвала считаются как на
 *     витрине (`footerLayout`, колонка одиночных ссылок — тоже колонка),
 *     одиночные ссылки отклоняются при SITE_MENU_FOOTER_REJECT_LOOSE;
 *   - `read` — чтение снимка и импорт: правила колонок подвала (лимит,
 *     одиночные ссылки) не проверяются — старая ревизия читается как есть,
 *     а не заменяется меню по умолчанию; структура (уровни, поля) — как при
 *     записи.
 */
export type SiteMenuValidateMode = "write" | "read";

/** Максимальная длина подписи пункта. */
export const SITE_MENU_LABEL_MAX = 80;

/** Проверка полей одного пункта — повтор CHECK `SiteDraftMenuItem_target_check`. */
export function checkMenuNodeFields(node: SiteMenuNodeFields, path: string): SiteMenuIssue[] {
  const issues: SiteMenuIssue[] = [];
  const add = (code: SiteMenuIssue["code"], message: string): void => {
    issues.push({ path, code, message });
  };
  if (node.label === null || node.label.trim() === "") {
    if (node.linkType !== "GROUP") add("site_menu_label_required", "Укажите подпись пункта");
  } else if (node.label.length > SITE_MENU_LABEL_MAX) {
    add("site_menu_label_too_long", `Подпись — не длиннее ${SITE_MENU_LABEL_MAX} знаков`);
  }
  const ownField =
    node.linkType in SITE_MENU_TARGET_FIELD
      ? SITE_MENU_TARGET_FIELD[node.linkType as TargetLinkType]
      : null;
  for (const field of TARGET_FIELDS) {
    if (field !== ownField && node[field] !== null) {
      add("site_menu_target_mismatch", "Цель пункта не совпадает с его типом");
      break;
    }
  }
  if ((node.systemRoute !== null) !== (node.linkType === "SYSTEM")) {
    add("site_menu_system_route_required", "Раздел сайта указывается только у пункта «Раздел сайта»");
  }
  if (node.linkType === "URL") {
    if (node.url === null || !isSafeMenuUrl(node.url)) {
      add("site_menu_url_invalid", "Адрес — путь вида /about или ссылка, начинающаяся с https://");
    }
  } else if (node.url !== null) {
    add("site_menu_target_mismatch", "Адрес указывается только у пункта «Адрес»");
  }
  if (node.autoLimit !== null) {
    const max = SITE_LIMITS.catalogCategoriesAutoLimit;
    if (
      node.linkType !== "CATALOG_CATEGORIES" ||
      !Number.isInteger(node.autoLimit) ||
      node.autoLimit < 1 ||
      node.autoLimit > max
    ) {
      add("site_menu_auto_limit_invalid", `Категорий в автосписке — от 1 до ${max}`);
    }
  }
  return issues;
}

/**
 * Проверка дерева одного места: поля каждого пункта, два уровня (SB15),
 * группа и автосписок категорий — только на верхнем уровне, у автосписка
 * нет своих детей, в подвале не больше трёх колонок (db-design-r1 §6;
 * SB113 — пустая колонка тоже в лимите; счёт — `footerLayout`).
 */
export function validateMenuTree(
  location: SiteMenuLocation,
  nodes: readonly SiteMenuNode[],
  options: { mode?: SiteMenuValidateMode; rejectFooterLoose?: boolean } = {},
): SiteMenuIssue[] {
  const mode = options.mode ?? "write";
  const rejectLoose = options.rejectFooterLoose ?? SITE_MENU_FOOTER_REJECT_LOOSE;
  const issues: SiteMenuIssue[] = [];
  const walk = (list: readonly SiteMenuNode[], depth: number, prefix: string): void => {
    list.forEach((node, index) => {
      const path = `${prefix}.${index}`;
      issues.push(...checkMenuNodeFields(node, path));
      if (depth > 1 && (node.linkType === "GROUP" || node.linkType === "CATALOG_CATEGORIES")) {
        issues.push({
          path,
          code: "site_menu_group_nested",
          message: "Группа и список категорий — только на верхнем уровне меню",
        });
      }
      if (node.linkType === "CATALOG_CATEGORIES" && node.children.length > 0) {
        issues.push({
          path,
          code: "site_menu_auto_list_children",
          message: "У списка категорий пункты собираются сами — свои добавить нельзя",
        });
      }
      if (node.children.length > 0 && depth >= SITE_LIMITS.menuDepth) {
        issues.push({
          path,
          code: "site_menu_too_deep",
          message: `Уровней меню — не больше ${SITE_LIMITS.menuDepth}`,
        });
      }
      walk(node.children, depth + 1, `${path}.children`);
    });
  };
  walk(nodes, 1, location);
  // Колонки подвала — правило записи. Сохранённый снимок с лишней колонкой
  // (старые данные, data-миграция П3 у магазина с тремя группами и
  // одиночными ссылками) читается как есть: иначе readMenus заменил бы всё
  // меню сайта умолчанием.
  if (location === "FOOTER" && mode === "write") {
    const columns = footerLayout(nodes).columns.length;
    if (rejectLoose) {
      nodes.forEach((node, index) => {
        if (node.linkType === "GROUP" || node.linkType === "CATALOG_CATEGORIES") return;
        issues.push({
          path: `${location}.${index}`,
          code: "site_menu_footer_loose_item",
          message: "В подвале каждый пункт стоит в колонке — добавьте его в колонку или создайте колонку без заголовка",
        });
      });
    }
    if (columns > SITE_LIMITS.footerColumns) {
      issues.push({
        path: location,
        code: "site_menu_footer_columns",
        message: `Колонок в подвале — не больше ${SITE_LIMITS.footerColumns}`,
      });
    }
  }
  return issues;
}

// ── zod-схема снимка `SiteRevision.menus` (чтение старых ревизий, импорт) ──

const id = z.string().min(1).max(64).nullable();

const nodeFieldsShape = {
  label: z.string().max(SITE_MENU_LABEL_MAX).nullable(),
  linkType: z.enum(SITE_MENU_LINK_TYPES),
  sitePageId: id,
  categoryId: id,
  contentCategoryId: id,
  contentTagId: id,
  legalDocumentId: id,
  systemRoute: z.enum(SITE_SYSTEM_ROUTES).nullable(),
  url: z.string().max(2000).nullable(),
  autoLimit: z.number().int().nullable(),
  openInNewTab: z.boolean(),
  isHidden: z.boolean(),
};

// Два уровня заданы структурой: у второго уровня детей нет.
const leafNodeSchema = z.strictObject({ ...nodeFieldsShape, children: z.array(z.never()).max(0) });
const topNodeSchema = z.strictObject({ ...nodeFieldsShape, children: z.array(leafNodeSchema) });

export const siteMenusSnapshotSchema = z
  .strictObject({ HEADER: z.array(topNodeSchema), FOOTER: z.array(topNodeSchema) })
  .superRefine((menus, ctx) => {
    for (const location of SITE_MENU_LOCATIONS) {
      for (const issue of validateMenuTree(location, menus[location], { mode: "read" })) {
        ctx.addIssue({ code: "custom", path: issue.path.split("."), message: issue.message });
      }
    }
  });
